import Button from 'ext-ui/components/Button/Button'
import { cta_linkTextPrimary } from 'ext-ui/style/colors'
import CheckmarkIcon from 'ext-ui/svg/icon/CheckmarkIcon'
import React, { ReactElement, useState } from 'react'
import { useSelector } from 'react-redux'
import { ConfirmDowngradeSubscriptionText } from 'src/constants'
import { translateNode } from 'src/localization/translateNode'
import { useTranslate } from 'src/localization/Trns'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'

import style from './CancelSubscriptionConfirm.style'
import DeactivationNotice from './DeactivationNotice'

interface CancelSubscriptionConfirmProps {
  onConfirm: () => void
  onCancel: () => void
  billingCycleEnd: string | null
  usage: number | undefined
}

const PRO_FEATURES: { icon: 'check' | 'magical'; text: string }[] = [
  { icon: 'check', text: 'Pay per user' },
  { icon: 'check', text: 'Up to 500 new virtual cards per month' },
  { icon: 'check', text: 'Up to 500 virtual cards active at any time' },
  { icon: 'magical', text: 'Advanced receipt management' },
  {
    icon: 'magical',
    text: 'Expanded expense categories, labels, and automatic coding',
  },
  { icon: 'check', text: 'Automated monthly expense reports' },
  { icon: 'check', text: 'QuickBooks Online integration' },
]

// Filled three-star sparkle — matches Figma node 23739:56749 feature row
// icon. Fill is hardcoded to brand AI purple (#4F3FDD) to match
// MagicalAIIcon's brand convention.
function MagicalSparkleIcon(): ReactElement {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M9.46666 5.32602C9.43163 5.23042 9.36809 5.14789 9.28463 5.08958C9.20117 5.03127 9.10181 5 9 5C8.89819 5 8.79883 5.03127 8.71537 5.08958C8.63191 5.14789 8.56837 5.23042 8.53334 5.32602L8.05891 6.62333C7.68603 7.64042 7.09608 8.56408 6.33008 9.33008C5.56408 10.0961 4.64042 10.686 3.62333 11.0589L2.32602 11.5333C2.23042 11.5684 2.14789 11.6319 2.08958 11.7154C2.03127 11.7988 2 11.8982 2 12C2 12.1018 2.03127 12.2012 2.08958 12.2846C2.14789 12.3681 2.23042 12.4316 2.32602 12.4667L3.62333 12.9411C4.64042 13.314 5.56408 13.9039 6.33008 14.6699C7.09608 15.4359 7.68603 16.3596 8.05891 17.3767L8.53334 18.674C8.56837 18.7696 8.63191 18.8521 8.71537 18.9104C8.79883 18.9687 8.89819 19 9 19C9.10181 19 9.20117 18.9687 9.28463 18.9104C9.36809 18.8521 9.43163 18.7696 9.46666 18.674L9.94109 17.3767C10.314 16.3596 10.9039 15.4359 11.6699 14.6699C12.4359 13.9039 13.3596 13.314 14.3767 12.9411L15.674 12.4667C15.7696 12.4316 15.8521 12.3681 15.9104 12.2846C15.9687 12.2012 16 12.1018 16 12C16 11.8982 15.9687 11.7988 15.9104 11.7154C15.8521 11.6319 15.7696 11.5684 15.674 11.5333L14.3767 11.0589C13.3596 10.686 12.4359 10.0961 11.6699 9.33008C10.9039 8.56408 10.314 7.64042 9.94109 6.62333L9.46666 5.32602Z"
        fill="#4F3FDD"
      />
      <path
        d="M18.1625 2.05094C18.2101 2.0841 18.2465 2.13107 18.2667 2.18552L18.5379 2.92619C18.7513 3.50754 19.0887 4.0355 19.5266 4.4734C19.9645 4.9113 20.4925 5.24866 21.0738 5.46206L21.8145 5.73325C21.8689 5.75346 21.9159 5.78986 21.9491 5.83754C21.9822 5.88523 22 5.94192 22 6C22 6.05808 21.9822 6.11478 21.9491 6.16246C21.9159 6.21014 21.8689 6.24654 21.8145 6.26675L21.0738 6.53794C20.4925 6.75134 19.9645 7.0887 19.5266 7.5266C19.0887 7.9645 18.7513 8.49246 18.5379 9.07381L18.2667 9.81448C18.2465 9.86894 18.2101 9.9159 18.1625 9.94906C18.1148 9.98223 18.0581 10 18 10C17.9419 10 17.8852 9.98223 17.8375 9.94906C17.7899 9.9159 17.7535 9.86894 17.7333 9.81448L17.4621 9.07381C17.2487 8.49246 16.9113 7.9645 16.4734 7.5266C16.0355 7.0887 15.5075 6.75134 14.9262 6.53794L14.1855 6.26675C14.1311 6.24654 14.0841 6.21014 14.0509 6.16246C14.0178 6.11478 14 6.05808 14 6C14 5.94192 14.0178 5.88523 14.0509 5.83754C14.0841 5.78986 14.1311 5.75346 14.1855 5.73325L14.9262 5.46206C15.5075 5.24866 16.0355 4.9113 16.4734 4.4734C16.9113 4.0355 17.2487 3.50754 17.4621 2.92619L17.7333 2.18552C17.7535 2.13107 17.7899 2.0841 17.8375 2.05094C17.8852 2.01778 17.9419 2 18 2C18.0581 2 18.1148 2.01778 18.1625 2.05094Z"
        fill="#4F3FDD"
      />
      <path
        d="M16.1221 16.0385C16.1579 16.0636 16.1851 16.099 16.2 16.1401L16.4032 16.6953C16.5631 17.1312 16.8159 17.527 17.1441 17.8554C17.4724 18.1837 17.8681 18.4367 18.3039 18.5968L18.8599 18.8C18.901 18.8149 18.9364 18.8421 18.9615 18.8779C18.9866 18.9137 19 18.9563 19 19C19 19.0437 18.9866 19.0863 18.9615 19.1221C18.9364 19.1579 18.901 19.1851 18.8599 19.2L18.3039 19.4032C17.8681 19.5633 17.4724 19.8163 17.1441 20.1446C16.8159 20.473 16.5631 20.8689 16.4032 21.3047L16.2 21.8599C16.1851 21.901 16.1579 21.9364 16.1221 21.9615C16.0863 21.9866 16.0437 22 16 22C15.9563 22 15.9137 21.9866 15.8779 21.9615C15.8421 21.9364 15.8149 21.901 15.8 21.8599L15.5968 21.3047C15.4369 20.8689 15.1841 20.473 14.8558 20.1446C14.5276 19.8163 14.1319 19.5633 13.6961 19.4032L13.1401 19.2C13.099 19.1851 13.0636 19.1579 13.0385 19.1221C13.0134 19.0863 13 19.0437 13 19C13 18.9563 13.0134 18.9137 13.0385 18.8779C13.0636 18.8421 13.099 18.8149 13.1401 18.8L13.6961 18.5968C14.1319 18.4367 14.5276 18.1837 14.8558 17.8554C15.1841 17.527 15.4369 17.1312 15.5968 16.6953L15.8 16.1401C15.8149 16.099 15.8421 16.0636 15.8779 16.0385C15.9137 16.0134 15.9563 16 16 16C16.0437 16 16.0863 16.0134 16.1221 16.0385Z"
        fill="#4F3FDD"
      />
    </svg>
  )
}

// Severe triangle icon — literal port of prototype WarnBanner.DefaultWarnIcon
// (severe variant). Fill + stroke both ride on currentColor so the parent
// banner's `color: tag_colorTagSevereText` drives both, and the fill is
// flipped to tag_colorTagSevereBackground via the .style.ts wrapper class.
function SevereTriangleIcon(): ReactElement {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
      className="CancelSubscriptionConfirm-banner-icon"
    >
      <path
        d="M12 2l11 19H1L12 2z"
        className="CancelSubscriptionConfirm-banner-icon-triangle"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M12 9v5M12 17h.01"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}

export default function CancelSubscriptionConfirm(
  props: CancelSubscriptionConfirmProps,
): ReactElement {
  const { onConfirm, onCancel, billingCycleEnd, usage } = props
  const STARTER_VIRTUAL_CARD_LIMIT = 100
  const overBy =
    typeof usage === 'number'
      ? Math.max(0, usage - STARTER_VIRTUAL_CARD_LIMIT)
      : 0
  // Name an exact count only when usage is known AND over the limit; else
  // "excess virtual cards" (no misleading "0").
  const noticeWithinLimit = !(
    typeof usage === 'number' && usage > STARTER_VIRTUAL_CARD_LIMIT
  )
  const translate = useTranslate()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const confirmPhrase = ConfirmDowngradeSubscriptionText[locale]

  const [confirmationText, setConfirmationText] = useState('')
  const confirmationValid =
    confirmationText.trim().toUpperCase() === confirmPhrase

  return (
    <div css={style} data-testid="cancel-step-confirm">
      <div className="CancelSubscriptionConfirm-content">
        <div className="CancelSubscriptionConfirm-upper">
          <div className="CancelSubscriptionConfirm-title-group">
            <h2 className="CancelSubscriptionConfirm-header">
              {translate('Confirm Pro plan downgrade')}
            </h2>
            <p className="CancelSubscriptionConfirm-subheader">
              {billingCycleEnd
                ? translate(
                    'Your downgrade to Starter will take effect on ${billingCycleEnd}.',
                    { billingCycleEnd },
                  )
                : translate(
                    'Your downgrade to Starter will take effect at the end of your billing cycle.',
                  )}
            </p>
          </div>

          <div
            className="CancelSubscriptionConfirm-banner"
            data-testid="cancel-confirm-banner"
          >
            <SevereTriangleIcon />
            <DeactivationNotice
              billingCycleEnd={billingCycleEnd}
              overBy={overBy}
              withinLimit={noticeWithinLimit}
            />
          </div>

          <div className="CancelSubscriptionConfirm-features">
            <div className="CancelSubscriptionConfirm-features-inner">
              <h3 className="CancelSubscriptionConfirm-features-header">
                {translate('Pro features you will lose:')}
              </h3>
              <div className="CancelSubscriptionConfirm-features-list">
                {PRO_FEATURES.map((feature) => (
                  <div
                    key={feature.text}
                    className="CancelSubscriptionConfirm-feature-row"
                  >
                    <span
                      aria-hidden="true"
                      className="CancelSubscriptionConfirm-feature-icon"
                    >
                      {feature.icon === 'check' ? (
                        <CheckmarkIcon fill={cta_linkTextPrimary} />
                      ) : (
                        <MagicalSparkleIcon />
                      )}
                    </span>
                    <p className="CancelSubscriptionConfirm-feature-text">
                      {translate(feature.text)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
            <a
              href="https://www.paywithextend.com/pricing"
              className="CancelSubscriptionConfirm-view-all"
              target="_blank"
              rel="noopener noreferrer"
            >
              {translate('View all features')}
            </a>
          </div>
        </div>

        <div className="CancelSubscriptionConfirm-confirm-row">
          <label
            className="CancelSubscriptionConfirm-confirm-label"
            htmlFor="cancel-confirm-input"
          >
            {translateNode('Confirm by typing ${downgrade} below:', {
              downgrade: (
                <span className="CancelSubscriptionConfirm-confirm-phrase">
                  {`“${confirmPhrase}”`}
                </span>
              ),
            })}
          </label>
          <input
            id="cancel-confirm-input"
            className="CancelSubscriptionConfirm-input"
            type="text"
            value={confirmationText}
            onChange={(e): void =>
              setConfirmationText(e.target.value.toUpperCase())
            }
            placeholder={translate('Please type "${phrase}"', {
              phrase: confirmPhrase,
            })}
            autoComplete="off"
            spellCheck={false}
            data-testid="cancel-submission-input"
          />
        </div>
      </div>

      <div className="CancelSubscriptionConfirm-buttons">
        <Button
          variant="no-border"
          onClick={onCancel}
          data-testid="cancel-submission-cancel"
        >
          {translate('Keep Pro')}
        </Button>
        <Button
          variant="severe"
          onClick={onConfirm}
          disabled={!confirmationValid}
          data-testid="cancel-submission-downgrade"
        >
          {translate('Downgrade Plan')}
        </Button>
      </div>
    </div>
  )
}
